import { Button } from "@/modules/irohana-components/elements/Button";
import { Form } from "@/modules/irohana-components/elements/Form";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { useToast } from "@/hooks/useToast";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { orange } from "@/theme/colors";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { AppRoutes } from "@/routes/config";
import { TMaintenanceForm } from "@/features/system-setting/types/maintenanceForm";
import { InputDateTimePicker } from "@/modules/irohana-components/elements/InputDateTimePicker";
import { maintenanceSchema } from "@/features/system-setting/schema/maintenanceSchema";
import { convertMaintenanceSettingFormToUpdateReq } from "@/features/system-setting/utils/converts";
import { updateMaintenanceSetting } from "@/api/services/admin/system-setting";
import { Select } from "@/modules/irohana-components/elements/Select";
import { Textarea } from "@/modules/irohana-components/elements/Textarea";
import { useEnum } from "@/api/services/global/enum";

const initForm: TMaintenanceForm = {
  maintenanceMode: "",
  modeStartDateTime: undefined,
  modeEndDateTime: undefined,
  maintenanceMessage: "",
};

type TMaintenanceFormProps = {
  readonly?: boolean;
  previousData?: Partial<TMaintenanceForm>;
};

export const MaintenanceForm: FC<TMaintenanceFormProps> = ({ readonly, previousData }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: maintenanceModeOptions, isLoading: isLoadingMaintenanceMode } = useEnum("EMaintenanceMode");
  const methods = useForm<TMaintenanceForm>({
    defaultValues: previousData ?? initForm,
    resolver: yupResolver(maintenanceSchema),
    mode: "onBlur",
  });
  const { control } = methods;

  const onSubmit = async (data: TMaintenanceForm) => {
    try {
      const payload = convertMaintenanceSettingFormToUpdateReq(data);
      const rs = await updateMaintenanceSetting(payload);
      navigate(AppRoutes.maintenanceSetting);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SYSTEM_SETTING.FETCH_MAINTENANCE_DETAIL] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={maintenanceSchema}>
      <Stack gap={3} paddingBottom={3}>
        <Typography variant="head20Bold">メンテナンスツール</Typography>
        <Typography variant="body14" color={orange[80]}>
          日時を設定後、切替日のAM9:00に重要なお知らせとして画面上部に表示されます。
          <br />
          このお知らせはメールが自動配信されないため、緊急メンテナンス時は事前にお知らせしてください。
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Stack gap={3}>
              <FormField control={control} name="maintenanceMode">
                <Select options={maintenanceModeOptions ?? []} loading={isLoadingMaintenanceMode} />
              </FormField>
            </Stack>
          </Grid>
          <Grid item xs={8}></Grid>
          <Grid item xs={4}>
            <Stack gap={3}>
              <FormField control={control} name="modeStartDateTime">
                <InputDateTimePicker timezone="Asia/Tokyo" />
              </FormField>
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Stack gap={3}>
              <FormField control={control} name="modeEndDateTime">
                <InputDateTimePicker timezone="Asia/Tokyo" />
              </FormField>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="maintenanceMessage">
              <Textarea minRows={5} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      {!readonly && (
        <Box textAlign="right" mt={6}>
          <Button type="submit" size="sm">
            設定
          </Button>
        </Box>
      )}
    </Form>
  );
};
